import { Link, useLocation } from 'react-router-dom';
import { ReactNode } from 'react';
import { Tag } from '../../atoms/Tag';

interface SidebarItemProps {
  to?: string;
  icon: ReactNode;
  label: string;
  matchPaths?: string[];
  comingSoon?: boolean;
  badge?: ReactNode;
  collapsed?: boolean;
  onClick?: () => void;
}

export const SidebarItem = ({
  to,
  icon,
  label,
  matchPaths,
  comingSoon = false,
  badge,
  collapsed = false,
  onClick,
}: SidebarItemProps) => {
  const location = useLocation();

  const isActive = (() => {
    if (comingSoon || !to) return false;
    const paths = matchPaths ?? [to];
    return paths.some((p) =>
      p === '/' ? location.pathname === '/' : location.pathname.startsWith(p),
    );
  })();

  const baseClasses =
    'group relative flex items-center gap-2.5 rounded-md px-2.5 h-9 text-sm transition-colors';

  const stateClasses = comingSoon
    ? 'text-dark-invirtus/35 cursor-default font-medium'
    : isActive
      ? 'bg-dark-invirtus-soft text-dark-invirtus font-semibold'
      : 'text-dark-invirtus/70 hover:bg-dark-invirtus-soft/60 hover:text-dark-invirtus font-medium';

  const content = (
    <>
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4 ${
          isActive ? 'text-dark-invirtus' : ''
        }`}
        aria-hidden="true"
      >
        {icon}
      </span>
      {!collapsed && (
        <>
          <span className="flex-1 truncate font-body">{label}</span>
          {comingSoon && (
            <Tag intent="secondary" variant="subtle" className="uppercase" size="sm">
              Soon
            </Tag>
          )}
          {!comingSoon && badge && <span className="shrink-0">{badge}</span>}
        </>
      )}
    </>
  );

  if (comingSoon || !to) {
    return (
      <div
        className={`${baseClasses} ${stateClasses}`}
        title={collapsed ? label : undefined}
        aria-disabled={comingSoon || !to}
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`${baseClasses} ${stateClasses}`}
      title={collapsed ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
    >
      {content}
    </Link>
  );
};
